<script lang="ts">
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from "$lib/components/ui/dialog";
  import { Input } from "$lib/components/ui/input";
  import { Label } from "$lib/components/ui/label";
  import { Button } from "$lib/components/ui/button";

  import { overlay } from "$lib/stores/ui";
  import { doLogin } from "$lib/stores/auth";
  import { t, type TranslationKey } from "$lib/i18n";
  import { RegisterUser } from "$lib/services/requests";
  import type { Role } from "@briefmarken_katalog/types";

  const isOpen = overlay('registerUser');

  let name = "";
  let email = "";
  let password = "";
  let role: Role | "" = "";
  let error = "";

  const roles: Role[] = ["Viewer", "Contributor", "Reviewer", "Admin"];

  function reset() {
    name = "";
    email = "";
    password = "";
    role = "";
    error = "";
  }

  async function handleSubmit() {
    if (!name.trim()) { error = $t('registerUser.errorNameEmpty'); return; }
    if (!email.trim()) { error = $t('registerUser.errorEmailEmpty'); return; }
    if (!password.trim()) { error = $t('registerUser.errorPasswordEmpty'); return; }
    if (!role) { error = $t('registerUser.errorRoleNotSet'); return; }

    error = "";
    try {
      await new RegisterUser({ name: name.trim(), email: email.trim(), password, role }, doLogin).execute();
      isOpen.set(false);
      reset();
    } catch (e: any) {
      error = e.message ?? "Unknown error";
    }
  }
</script>

<Dialog bind:open={$isOpen} onOpenChange={(open) => { if (!open) reset(); }}>
  <DialogContent>
    <DialogHeader>
      <DialogTitle>{$t('registerUser.title')}</DialogTitle>
      <DialogDescription>{$t('registerUser.description')}</DialogDescription>
    </DialogHeader>

    <div class="grid gap-4 py-4">
      <div class="grid grid-cols-4 items-center gap-4">
        <Label for="reg-name" class="text-right">{$t('registerUser.name')}</Label>
        <Input id="reg-name" bind:value={name} placeholder={$t('registerUser.namePlaceholder')} class="col-span-3" />
      </div>
      <div class="grid grid-cols-4 items-center gap-4">
        <Label for="reg-email" class="text-right">{$t('registerUser.email')}</Label>
        <Input id="reg-email" type="email" bind:value={email} class="col-span-3" />
      </div>
      <div class="grid grid-cols-4 items-center gap-4">
        <Label for="reg-password" class="text-right">{$t('registerUser.password')}</Label>
        <Input id="reg-password" type="password" bind:value={password} class="col-span-3" />
      </div>
      <div class="grid grid-cols-4 items-center gap-4">
        <Label for="reg-role" class="text-right">{$t('registerUser.role')}</Label>
        <select
          id="reg-role"
          bind:value={role}
          class="col-span-3 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value="" disabled></option>
          {#each roles as r}
            <option value={r}>{$t(`roles.${r}` as TranslationKey)}</option>
          {/each}
        </select>
      </div>

      {#if error}
        <p class="text-sm text-destructive">{error}</p>
      {/if}
    </div>

    <form onsubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
      <DialogFooter>
        <Button type="submit">{$t('registerUser.button')}</Button>
      </DialogFooter>
    </form>
  </DialogContent>
</Dialog>
